"use client";
import Cookies from "js-cookie";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import useSWR from "swr";
import { API } from "../StoreComponents/conflict";
import NotFoundPage from "../StoreComponents/NotFoundPage";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

// Fetchers
const fetcher1 = (url) =>
  API.get(url, {
    headers: {
      search: `%%`,
    },
  }).then((res) => res.data.data);

const fetcher = (url) => API.get(url).then((res) => res.data.data);

const Dashboard = () => {
  const logInStatus = parseInt(Cookies.get("Role"));
  const [loading, setLoading] = useState(true);

  // Get View Requisition
  const {
    data: viewRequisition,
    error: error1,
    mutate,
  } = useSWR(`store/requisition/byId?page=${0}&limit=${1000000}`, fetcher1);

  const { data: viewAdminData, error: error2 } = useSWR(
    `store/warehouse/admin/dashboard`,
    fetcher
  );

  useEffect(() => {
    if (viewRequisition || error1 || viewAdminData || error2) {
      setLoading(false);
    }
  }, [viewRequisition, error1, viewAdminData, error2]);

  const formatNumber = (num) => {
    if (num === undefined || num === null) return "0";
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Updated Card Styles with more attractive colors
  const cardStyles = {
    teal: {
      gradient: "linear-gradient(135deg, #00b4db, #0083b0)",
      shadowColor: "rgba(0, 180, 219, 0.4)",
      iconColor: "#00b4db",
    },
    emerald: {
      gradient: "linear-gradient(135deg, #2ecc71, #27ae60)",
      shadowColor: "rgba(46, 204, 113, 0.4)",
      iconColor: "#2ecc71",
    },
    violet: {
      gradient: "linear-gradient(135deg, #9b59b6, #8e44ad)",
      shadowColor: "rgba(155, 89, 182, 0.4)",
      iconColor: "#9b59b6",
    },
    sunset: {
      gradient: "linear-gradient(135deg, #e74c3c, #c0392b)",
      shadowColor: "rgba(231, 76, 60, 0.4)",
      iconColor: "#e74c3c",
    },
    gold: {
      gradient: "linear-gradient(135deg, #f39c12, #e67e22)",
      shadowColor: "rgba(243, 156, 18, 0.4)",
      iconColor: "#f39c12",
    },
    ocean: {
      gradient: "linear-gradient(135deg, #3498db, #2980b9)",
      shadowColor: "rgba(52, 152, 219, 0.4)",
      iconColor: "#3498db",
    },
    coral: {
      gradient: "linear-gradient(135deg, #ff7f50, #ff6347)",
      shadowColor: "rgba(255, 127, 80, 0.4)",
      iconColor: "#ff7f50",
    },
    lavender: {
      gradient: "linear-gradient(135deg, #a569bd, #8e44ad)",
      shadowColor: "rgba(165, 105, 189, 0.4)",
      iconColor: "#a569bd",
    },
  };

  const renderStatCard = (title, value, icon, color, link) => {
    return (
      <div className="col-md-6 col-sm-6 col-lg-6 col-xl-3 mb-4">
        <div
          className="card h-100 border-0"
          style={{
            background: cardStyles[color].gradient,
            borderRadius: "15px",
            boxShadow: `0 6px 20px ${cardStyles[color].shadowColor}`,
            transition: "all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)",
            overflow: "hidden",
            position: "relative",
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = "translateY(-5px)";
            e.currentTarget.style.boxShadow = `0 12px 24px ${cardStyles[color].shadowColor}`;
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = `0 6px 20px ${cardStyles[color].shadowColor}`;
          }}
        >
          {/* Animated background element */}
          <div
            style={{
              position: "absolute",
              top: "-50%",
              left: "-50%",
              width: "200%",
              height: "200%",
              background: `radial-gradient(circle, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 70%)`,
              transform: "rotate(30deg)",
              transition: "all 0.5s ease",
              opacity: 0,
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.opacity = "1";
              e.currentTarget.style.left = "0%";
              e.currentTarget.style.top = "0%";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.opacity = "0";
              e.currentTarget.style.left = "-50%";
              e.currentTarget.style.top = "-50%";
            }}
          ></div>

          <div className="card-body d-flex flex-column position-relative z-index-1">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="text-white font-weight-bold mb-0">{title}</h5>
              <div
                className="rounded-circle bg-white p-3 d-flex align-items-center justify-content-center"
                style={{
                  width: "50px",
                  height: "50px",
                  boxShadow: `0 4px 8px rgba(0,0,0,0.1)`
                }}
              >
                <i
                  className={icon}
                  style={{
                    fontSize: "24px",
                    color: cardStyles[color].iconColor,
                  }}
                />
              </div>
            </div>
            <h2
              className="text-white mb-3"
              style={{
                fontSize: "2.5rem",
                fontWeight: "700",
                textShadow: "0 2px 4px rgba(0,0,0,0.1)"
              }}
            >
              {formatNumber(value)}
            </h2>
            <div className="mt-auto">
              {link && (
                <Link
                  to={link}
                  className="text-white d-flex align-items-center"
                  style={{
                    fontWeight: "500",
                    textDecoration: "none",
                    transition: "all 0.2s ease"
                  }}
                >
                  More Info
                  <i
                    className="fa fa-arrow-circle-right ml-2"
                    style={{
                      marginLeft: "8px",
                      transition: "transform 0.2s ease"
                    }}
                  ></i>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div
        className="page-wrapper d-flex justify-content-center align-items-center"
        style={{ minHeight: "100vh", background: "#f8f9fa" }}
      >
        <div className="text-center">
          <div
            className="spinner-grow text-primary"
            role="status"
            style={{ width: "3rem", height: "3rem" }}
          >
            <span className="sr-only">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading Dashboard...</p>
        </div>
      </div>
    );
  }

  const chartData = viewAdminData?.chartDataArray || Array(12).fill({
    name: "Month",
    Approved: 0,
    Denied: 0,
    TotalQuantities: 0
  });

  return (
    <>
      <Helmet>
        <title>Dashboard - STORE</title>
        <meta name="description" content="STORE" />
      </Helmet>
      <div className="page-wrapper" style={{ background: "#f8f9fa" }}>
        <div className="content container-fluid py-4">
          {/* Page Header */}
          <div className="page-header mb-4">
            <div className="row align-items-center">
              <div className="col-sm-12">
                <div
                  className="welcome-card p-4 bg-white rounded-lg shadow-sm mb-4"
                  style={{
                    background: "linear-gradient(135deg, #ffffff, #f8f9fa)",
                    border: "none",
                    borderLeft: "5px solid #3498db"
                  }}
                >
                  <h2
                    className="page-title mb-2"
                    style={{
                      fontWeight: "700",
                      color: "#2c3e50",
                      fontSize: "2rem"
                    }}
                  >
                    Welcome to Store Dashboard
                  </h2>
                  <p
                    className="text-muted"
                    style={{
                      fontSize: "1.1rem",
                      maxWidth: "600px"
                    }}
                  >
                    Monitor your store activities and performance with
                    real-time insights and analytics
                  </p>
                  <div className="mt-3 d-flex flex-wrap gap-2">
                    <span
                      className="badge me-2 p-2"
                      style={{
                        background: "linear-gradient(135deg, #3498db, #2980b9)",
                        color: "white",
                        borderRadius: "8px"
                      }}
                    >
                      Role: {[5, 24, 41, 2].includes(logInStatus) ? "Admin" : "User"}
                    </span>
                    <span
                      className="badge p-2"
                      style={{
                        background: "linear-gradient(135deg, #2ecc71, #27ae60)",
                        color: "white",
                        borderRadius: "8px"
                      }}
                    >
                      {new Date().toLocaleDateString("en-US", {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Statistics Cards */}
          <div className="row">
            {renderStatCard(
              "Products",
              viewRequisition?.tatalC?.TOTAL_PRODUCTS || 0,
              "fa fa-gift",
              "teal",
              "/requisition/view"
            )}

            {renderStatCard(
              "Requisition",
              viewRequisition?.tatalC?.TOTAL_REQUISITION || 0,
              "fa fa-newspaper-o",
              "violet",
              "/requisition/view"
            )}

            {renderStatCard(
              "Approved",
              viewRequisition?.tatalC?.TOTAL_APPROVED || 0,
              "la la-thumbtack",
              "emerald",
              "/requisition/view"
            )}

            {renderStatCard(
              "Denied",
              viewRequisition?.tatalC?.TOTAL_DENY || 0,
              "fa fa-times-circle",
              "sunset",
              "/requisition/view"
            )}
          </div>

          {/* Admin Dashboard Section */}
          {[5, 24, 41, 2].includes(logInStatus) && (
            <>
              <div className="row mb-4">
                <div className="col-md-7 mb-4 mb-md-0">
                  <div className="card shadow-sm h-100 border-0">
                    <div
                      className="card-header bg-white py-3 border-0"
                      style={{
                        background: "linear-gradient(135deg, #ffffff, #f8f9fa)",
                        borderBottom: "1px solid rgba(0,0,0,0.05)"
                      }}
                    >
                      <h5 className="mb-0 fw-bold">Requisition Statistics</h5>
                    </div>
                    <div className="card-body">
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                          data={chartData}
                          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" opacity={0.5} />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip
                            contentStyle={{
                              borderRadius: "8px",
                              border: "none",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
                            }}
                          />
                          <Legend />
                          <Bar
                            dataKey="Approved"
                            fill="#2ecc71"
                            radius={[4, 4, 0, 0]}
                          />
                          <Bar
                            dataKey="Denied"
                            fill="#e74c3c"
                            radius={[4, 4, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
                <div className="col-md-5">
                  <div className="card shadow-sm h-100 border-0">
                    <div
                      className="card-header bg-white py-3 border-0"
                      style={{
                        background: "linear-gradient(135deg, #ffffff, #f8f9fa)",
                        borderBottom: "1px solid rgba(0,0,0,0.05)"
                      }}
                    >
                      <h5 className="mb-0 fw-bold">Inventory Stocks</h5>
                    </div>
                    <div className="card-body">
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart
                          data={chartData}
                          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" opacity={0.5} />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip
                            contentStyle={{
                              borderRadius: "8px",
                              border: "none",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
                            }}
                          />
                          <Legend />
                          <Line
                            type="monotone"
                            dataKey="TotalQuantities"
                            stroke="#3498db"
                            strokeWidth={3}
                            dot={{ fill: "#3498db", r: 5 }}
                            activeDot={{ fill: "#2980b9", r: 7 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row mb-4">
                <div className="col-12">
                  <div className="card shadow-sm border-0 overflow-hidden">
                    <div
                      className="card-header bg-white py-3 border-0"
                      style={{
                        background: "linear-gradient(135deg, #ffffff, #f8f9fa)",
                        borderBottom: "1px solid rgba(0,0,0,0.05)"
                      }}
                    >
                      <h5 className="mb-0">Store Management</h5>
                      <p className="text-muted mb-0">
                        Inventory and requisition statistics
                      </p>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        {renderStatCard(
                          "Stock Products",
                          viewAdminData?.totalProducts || 0,
                          "fa fa-gift",
                          "ocean",
                          "/product/manageProducts"
                        )}

                        {renderStatCard(
                          "Stock Quantities",
                          parseInt(viewAdminData?.totalQuantites) || 0,
                          "fa fa-cubes",
                          "coral",
                          "/product/manageProducts"
                        )}

                        {renderStatCard(
                          "Total Requisitions",
                          viewAdminData?.totalrequisitions || 0,
                          "fa fa-newspaper-o",
                          "lavender",
                          "/requisitions/done"
                        )}

                        {renderStatCard(
                          "Approved Requisitions",
                          viewAdminData?.totalApprovedqty || 0,
                          "fa fa-check-circle",
                          "gold",
                          "/requisitions/done"
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="row mb-4">
                <div className="col-12">
                  <div className="card shadow-sm border-0">
                    <div
                      className="card-header bg-white py-3 border-0"
                      style={{
                        background: "linear-gradient(135deg, #ffffff, #f8f9fa)",
                        borderBottom: "1px solid rgba(0,0,0,0.05)"
                      }}
                    >
                      <h5 className="mb-0">Quick Actions</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        {[
                          {
                            title: "Manage Products",
                            icon: "fa fa-cubes",
                            color: "#00b4db",
                            link: "/product/manageProducts"
                          },
                          {
                            title: "View Requisitions",
                            icon: "fa fa-file-text-o",
                            color: "#9b59b6",
                            link: "/requisition/view"
                          },
                          {
                            title: "User Management",
                            icon: "fa fa-users",
                            color: "#f39c12",
                            link: "/systemaccess/userprofile"
                          },
                          {
                            title: "Settings",
                            icon: "fa fa-cog",
                            color: "#2ecc71",
                            link: "/settings/productList"
                          }
                        ].map((action, index) => (
                          <div className="col-md-3 col-sm-6 mb-3" key={index}>
                            <Link
                              to={action.link}
                              className="text-decoration-none"
                            >
                              <div
                                className="p-4 rounded text-center h-100 d-flex flex-column justify-content-center align-items-center"
                                style={{
                                  transition: "all 0.3s ease",
                                  background: "white",
                                  border: "none",
                                  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                                  borderRadius: "12px",
                                  height: "100%"
                                }}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.transform = "translateY(-5px)";
                                  e.currentTarget.style.boxShadow = `0 8px 24px ${hexToRgba(action.color, 0.2)}`;
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.transform = "translateY(0)";
                                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.05)";
                                }}
                              >
                                <div
                                  className="rounded-circle p-3 mb-3 d-flex align-items-center justify-content-center"
                                  style={{
                                    background: hexToRgba(action.color, 0.1),
                                    width: "60px",
                                    height: "60px"
                                  }}
                                >
                                  <i
                                    className={action.icon}
                                    style={{
                                      fontSize: "24px",
                                      color: action.color
                                    }}
                                  ></i>
                                </div>
                                <h5
                                  className="mb-0"
                                  style={{ color: "#2c3e50" }}
                                >
                                  {action.title}
                                </h5>
                              </div>
                            </Link>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

// Helper function to convert hex to rgba
function hexToRgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export default Dashboard;